import type { FC } from "react";

import type { DynamicPropertyToGroupEntity } from "src/app/features/DynamicPropertyGroup/types/DynamicPropertyToGroupEntity";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { Actions } from "../../../Table/types/types";
import type { AntCustomFormField, CommonFieldAttributes, SelectDynamicFormField } from "../../types/types";
import { Checkbox } from "../Checkbox/Checkbox";
import { SelectDynamic } from "../SelectDynamic/SelectDynamic";
import { DynamicPropertySelector } from "./DynamicPropertySelector";

export interface DynamicPropertyGroupPropertiesTableProps
    extends AntCustomFormField<DynamicPropertyToGroupEntity[]>,
        CommonFieldAttributes {}

export const DynamicPropertyGroupPropertiesTable: FC<DynamicPropertyGroupPropertiesTableProps> = ({ value = [], onChange, readOnly }) => {
    const onDataChanged = (data: DynamicPropertyToGroupEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onInput = (id: string | number, key: keyof DynamicPropertyToGroupEntity, newValue: string | boolean) => {
        const newData = value.map((row) => {
            if (row.id !== id) return row; // if item in table is not from toggled row, return as is

            return { ...row, [key]: newValue };
        });

        onDataChanged(newData);
    };

    const getColumnsConfig = (t: Function): ExtendedColumnsType<DynamicPropertyToGroupEntity>[] => {
        return [
            {
                title: t("dynamicProperties.id"),
                key: "dynamicPropertyId",
                dataIndex: "dynamicPropertyId",
            },
            {
                title: t("dynamicProperties.key"),
                key: "keyProp",
                dataIndex: "keyProp",
            },
            {
                title: t("dynamicProperties.name"),
                key: "name",
                dataIndex: "name",
            },
            {
                title: t("dynamicProperties.dataType"),
                key: "dataType",
                dataIndex: "dataType",
            },
            {
                title: t("dynamicProperties.validationPattern"),
                key: "validationPattern",
                dataIndex: "validationPattern",
            },
            {
                title: t("dynamicProperties.mandatory"),
                dataIndex: "mandatory",
                key: "mandatory",
                render: (_, record) => (
                    <Checkbox
                        name={`mandatory-${record.id}`}
                        label=""
                        value={record.mandatory ?? false}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.id!, "mandatory", newValue)}
                    />
                ),
            },
            {
                title: t("dynamicProperties.terminalExportType"),
                key: "terminalExportType",
                dataIndex: "terminalExportType",
                render: (_, record) => (
                    <SelectDynamic
                        name={`terminalExportType-${record.id}`}
                        label=""
                        value={record.terminalExportType}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.id!, "terminalExportType", newValue)}
                        fieldConfig={
                            {
                                urlToFetchOptions: "/configurations/dynamic-property-export-type",
                                optionDisplayPropName: "key",
                                optionValuePropName: "value",
                            } as SelectDynamicFormField
                        }
                    />
                ),
            },
            {
                title: t("dynamicProperties.centralExportType"),
                key: "centralExportType",
                dataIndex: "centralExportType",
                render: (_, record) => (
                    <SelectDynamic
                        name={`centralExportType-${record.id}`}
                        label=""
                        value={record.centralExportType}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.id!, "centralExportType", newValue)}
                        fieldConfig={
                            {
                                urlToFetchOptions: "/configurations/dynamic-property-export-type",
                                optionDisplayPropName: "key",
                                optionValuePropName: "value",
                            } as SelectDynamicFormField
                        }
                    />
                ),
            },
        ];
    };

    const selector = <DynamicPropertySelector key="selectPropertyToAdd" />;
    return (
        <PropertyTable<DynamicPropertyToGroupEntity>
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="dynamicPropertyId"
            tableData={value}
            readOnly={readOnly || false}
            elementSelector={selector}
            onDataChanged={onDataChanged}
            actions={[Actions.ADD, Actions.DELETE]}
        />
    );
};
